<template>
  <v-container fluid class="admin-page pa-2 pa-sm-4">
    <v-card v-if="!admin" class="login-card mx-auto" outlined>
      <v-card-title class="justify-center">Admin Login</v-card-title>
      <v-card-subtitle class="text-center">
        Sign in to start live connections or manage character sheets.
      </v-card-subtitle>
      <v-card-text>
        <v-form @submit.prevent="login">
          <v-text-field
            v-model.trim="adminUsername"
            label="User Name/Email"
            autocomplete="username"
            outlined
            dense
            :disabled="loginLoading"
          />
          <v-text-field
            v-model="adminPassword"
            label="Password"
            type="password"
            autocomplete="current-password"
            outlined
            dense
            :disabled="loginLoading"
            @keyup.enter="login"
          />
          <v-alert v-if="loginError" type="error" dense text class="mb-4">
            {{ loginError }}
          </v-alert>
          <v-btn
            type="submit"
            color="primary"
            block
            :loading="loginLoading"
            :disabled="!adminUsername || !adminPassword"
          >
            Login
          </v-btn>
        </v-form>
      </v-card-text>
    </v-card>

    <div v-else class="admin-shell mx-auto">
      <div class="d-flex flex-wrap align-center justify-space-between mb-3">
        <div>
          <h1 class="text-h5 text-sm-h4 mb-1">Character Administration</h1>
          <div class="text-body-2 grey--text text--darken-1">
            Live connection tools and character-sheet management
          </div>
        </div>
        <v-btn text color="primary" class="mt-2 mt-sm-0" @click="logout">
          Log out
        </v-btn>
      </div>

      <v-card outlined>
        <v-tabs
          v-model="activeTab"
          color="primary"
          grow
          show-arrows
        >
          <v-tab>Live Connections</v-tab>
          <v-tab>Character Sheets</v-tab>
        </v-tabs>

        <v-divider />

        <v-tabs-items v-model="activeTab">
          <v-tab-item>
            <div class="pa-3 pa-sm-5">
              <v-row>
                <v-col cols="12" md="8">
                  <v-card outlined height="100%">
                    <v-card-title class="text-h6">Connection Status</v-card-title>
                    <v-card-text>
                      <div class="text-caption grey--text text--darken-1">
                        Admin connection ID
                      </div>
                      <div class="connection-id font-weight-medium mb-4">
                        {{ connectionId || 'Connecting…' }}
                      </div>
                      <v-btn color="primary" :disabled="!adminSessionStarted" @click="generateNewId">
                        Generate New ID
                      </v-btn>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-card outlined height="100%" class="status-card">
                    <v-card-text class="text-center">
                      <div class="text-h4 primary--text">{{ connectedCharacters.length }}</div>
                      <div class="text-body-2">Connected</div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="2">
                  <v-card outlined height="100%" class="status-card">
                    <v-card-text class="text-center">
                      <div class="text-h4 orange--text text--darken-2">{{ pendingConnections.length }}</div>
                      <div class="text-body-2">Pending</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="5">
                  <v-card outlined height="100%">
                    <v-card-title class="text-h6">Pending Requests</v-card-title>
                    <v-card-text v-if="pendingConnections.length === 0" class="grey--text">
                      No pending connection requests.
                    </v-card-text>
                    <v-list v-else dense>
                      <v-list-item
                        v-for="connection in pendingConnections"
                        :key="`${connection.characterId}-${connection.characterPeerId}`"
                      >
                        <v-list-item-content>
                          <v-list-item-title>{{ connection.characterName }}</v-list-item-title>
                          <v-list-item-subtitle class="text-wrap">
                            ID: {{ connection.characterId }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-chip color="orange" text-color="white" small>Waiting</v-chip>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-col>

                <v-col cols="12" md="7">
                  <v-card outlined height="100%">
                    <v-card-title class="text-h6">Connected Characters</v-card-title>
                    <v-card-text v-if="connectedCharacters.length === 0" class="grey--text">
                      No characters are connected.
                    </v-card-text>
                    <v-list v-else dense>
                      <v-list-item
                        v-for="character in connectedCharacters"
                        :key="character.peerId"
                      >
                        <v-list-item-content>
                          <v-list-item-title>{{ character.name }}</v-list-item-title>
                          <v-list-item-subtitle class="text-wrap">
                            Peer ID: {{ character.peerId }}
                          </v-list-item-subtitle>
                          <v-list-item-subtitle class="text-wrap">
                            Character ID: {{ character.characterId || 'Not provided' }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action class="connection-actions">
                          <v-chip color="success" small class="mb-2">Connected</v-chip>
                          <v-btn
                            color="error"
                            small
                            outlined
                            @click="disconnectCharacter(character.peerId)"
                          >
                            Disconnect
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list>
                  </v-card>
                </v-col>
              </v-row>

              <v-card outlined class="mt-3">
                <v-card-title class="text-h6">Messages and Commands</v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" md="5">
                      <v-select
                        v-model="selectedCharacter"
                        :items="connectedCharacters"
                        item-text="name"
                        item-value="peerId"
                        label="Character"
                        outlined
                        dense
                        :disabled="connectedCharacters.length === 0"
                      />
                    </v-col>
                    <v-col cols="12" md="7">
                      <v-text-field
                        v-model="messageToSend"
                        label="Message"
                        outlined
                        dense
                        :disabled="connectedCharacters.length === 0"
                        @keyup.enter="sendMessage"
                      />
                    </v-col>
                  </v-row>

                  <div class="admin-actions">
                    <v-btn
                      color="success"
                      :disabled="!selectedCharacter || !messageToSend.trim()"
                      @click="sendMessage"
                    >
                      Send to Selected
                    </v-btn>
                    <v-btn
                      color="info"
                      :disabled="connectedCharacters.length === 0 || !messageToSend.trim()"
                      @click="sendToAll"
                    >
                      Send to All
                    </v-btn>
                    <v-divider vertical class="d-none d-md-block mx-1" />
                    <v-btn
                      color="primary"
                      small
                      :disabled="!selectedCharacter"
                      @click="sendCommand('refresh_character')"
                    >
                      Refresh Character
                    </v-btn>
                    <v-btn
                      color="success"
                      small
                      :disabled="!selectedCharacter"
                      @click="sendCommand('save_character')"
                    >
                      Save Character
                    </v-btn>
                    <v-btn
                      color="info"
                      small
                      :disabled="!selectedCharacter"
                      @click="sendCommand('ping')"
                    >
                      Ping
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>

              <v-expansion-panels v-if="receivedMessages.length > 0" class="mt-3">
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    Received Messages ({{ receivedMessages.length }})
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="d-flex justify-end mb-2">
                      <v-btn color="warning" small outlined @click="clearMessages">
                        Clear Messages
                      </v-btn>
                    </div>
                    <v-list dense>
                      <v-list-item
                        v-for="(message, index) in receivedMessages"
                        :key="`${message.peerId}-${index}`"
                      >
                        <v-list-item-content>
                          <v-list-item-title>
                            {{ message.timestamp }} — {{ message.from }}
                          </v-list-item-title>
                          <v-list-item-subtitle class="message-content">
                            {{ message.content }}
                          </v-list-item-subtitle>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </v-tab-item>

          <v-tab-item>
            <div class="pa-3 pa-sm-5">
              <div class="table-toolbar mb-3">
                <div>
                  <h2 class="text-h6 mb-1">Character Sheets</h2>
                  <div class="text-body-2 grey--text text--darken-1">
                    {{ characterSheets.length }} sheets loaded
                    <span v-if="selectedSheets.length">
                      · {{ selectedSheets.length }} selected
                    </span>
                  </div>
                </div>
                <div class="table-controls">
                  <v-text-field
                    v-model="characterSearch"
                    label="Search character sheets"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    hide-details
                    outlined
                    dense
                  />
                  <v-btn
                    color="error"
                    :disabled="selectedSheets.length === 0 || deleteLoading"
                    @click="openBulkDeleteDialog"
                  >
                    Delete selected
                  </v-btn>
                  <v-btn
                    color="primary"
                    outlined
                    :loading="characterSheetsLoading"
                    @click="loadCharacterSheets"
                  >
                    Refresh
                  </v-btn>
                </div>
              </div>

              <v-alert v-if="characterSheetsError" type="error" dense text>
                {{ characterSheetsError }}
              </v-alert>

              <v-data-table
                v-model="selectedSheets"
                :headers="characterHeaders"
                :items="characterSheets"
                :search="characterSearch"
                :loading="characterSheetsLoading"
                :items-per-page="10"
                :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
                item-key="id"
                show-select
                class="character-table"
              >
                <template v-slot:item.id="{ item }">
                  <span class="uuid-cell">{{ item.id }}</span>
                </template>
                <template v-slot:item.locked="{ item }">
                  <v-chip :color="item.locked ? 'error' : 'success'" small outlined>
                    {{ item.locked ? 'Locked' : 'Unlocked' }}
                  </v-chip>
                </template>
                <template v-slot:item.sync_revision="{ item }">
                  {{ item.sync_revision == null ? '—' : item.sync_revision }}
                </template>
                <template v-slot:item.character_data_last_change="{ item }">
                  {{ formatDate(item.character_data_last_change) }}
                </template>
                <template v-slot:item.instance_of="{ item }">
                  <div v-if="item.instance_of">
                    <v-chip color="deep-purple" text-color="white" x-small class="mb-1">
                      Instance
                    </v-chip>
                    <div class="uuid-cell text-caption">{{ item.instance_of }}</div>
                  </div>
                  <v-chip v-else color="primary" x-small outlined>Template</v-chip>
                </template>
                <template v-slot:item.actions="{ item }">
                  <div class="row-actions">
                    <v-btn icon small title="Copy UUID" @click="copyUuid(item.id)">
                      <v-icon small>mdi-content-copy</v-icon>
                    </v-btn>
                    <v-btn icon small title="Open character sheet" @click="openCharacterSheet(item.id)">
                      <v-icon small>mdi-open-in-new</v-icon>
                    </v-btn>
                    <v-btn icon small color="error" title="Delete character sheet" @click="openDeleteDialog(item)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </template>
                <template v-slot:no-data>
                  No character sheets were found.
                </template>
              </v-data-table>
            </div>
          </v-tab-item>
        </v-tabs-items>
      </v-card>
    </div>

    <v-dialog v-model="deleteDialog" :max-width="showDeleteReview ? '640' : '520'" persistent>
      <v-card>
        <v-card-title class="text-h6">
          {{ showDeleteReview
            ? `Delete ${deleteTargets.length} character sheet${deleteTargets.length === 1 ? '' : 's'}?`
            : 'Delete character sheet?' }}
        </v-card-title>
        <v-card-text v-if="deleteTargets.length">
          <v-alert type="warning" dense text>
            <template v-if="showDeleteReview">
              This permanently deletes the {{ deleteTargets.length }} sheet{{ deleteTargets.length === 1 ? '' : 's' }} listed below.
              Any active token bindings for these sheets will also be removed.
            </template>
            <template v-else>
              This permanently deletes <strong>{{ deleteTargets[0].character_name }}</strong>.
              Any active token binding for this sheet will also be removed.
            </template>
          </v-alert>

          <div v-if="showDeleteReview" class="delete-review mb-4">
            <div class="text-subtitle-2 mb-2">Review sheets to delete</div>
            <v-list dense class="delete-review-list">
              <v-list-item
                v-for="sheet in deleteTargets"
                :key="sheet.id"
              >
                <v-list-item-content>
                  <v-list-item-title>{{ sheet.character_name }}</v-list-item-title>
                  <v-list-item-subtitle class="text-wrap">
                    Owner: {{ sheet.owner_name || '—' }} · {{ sheet.id }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>

          <div class="mb-3">
            <template v-if="showDeleteReview">
              Type <strong>DELETE {{ deleteTargets.length }}</strong> to confirm.
            </template>
            <template v-else>
              Enter <strong>{{ deleteTargets[0].character_name }}</strong> to confirm.
            </template>
          </div>
          <v-text-field
            v-model="deleteConfirmation"
            :label="showDeleteReview ? 'Confirmation phrase' : 'Character name'"
            outlined
            dense
            :disabled="deleteLoading"
            @keyup.enter="deleteCharacterSheets"
          />
          <v-text-field
            v-model="deletePassword"
            label="Deletion password"
            type="password"
            autocomplete="off"
            outlined
            dense
            :disabled="deleteLoading"
            hint="Uses the Roll20 GM/admin deletion password."
            persistent-hint
            @keyup.enter="deleteCharacterSheets"
          />
          <v-alert v-if="deleteError" type="error" dense text class="mb-0">
            {{ deleteError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="deleteLoading" @click="closeDeleteDialog">Cancel</v-btn>
          <v-btn
            color="error"
            :loading="deleteLoading"
            :disabled="!canConfirmDelete"
            @click="deleteCharacterSheets"
          >
            {{ showDeleteReview ? `Delete ${deleteTargets.length}` : 'Delete' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { createClient } from '@supabase/supabase-js'
import PrivateConnector from './privateConnector.js'

export default {
  name: 'AdminPage',
  data() {
    return {
      activeTab: 0,
      admin: false,
      adminUsername: '',
      adminPassword: '',
      loginLoading: false,
      loginError: '',
      url: '/api/HttpTrigger1',
      supabaseUrl: 'https://kbjjqssxridfwtqjwquo.supabase.co',
      supabaseKey: 'sb_publishable_PspCtMt73rIksYz1s0DhJw_SoC43B_U',
      supabase: null,
      characterSheets: [],
      characterSheetsLoading: false,
      characterSheetsError: '',
      characterSearch: '',
      selectedSheets: [],
      characterHeaders: [
        { text: 'Character', value: 'character_name' },
        { text: 'Owner', value: 'owner_name' },
        { text: 'UUID', value: 'id' },
        { text: 'Status', value: 'instance_of' },
        { text: 'Lock', value: 'locked' },
        { text: 'Revision', value: 'sync_revision' },
        { text: 'Last Character Change', value: 'character_data_last_change' },
        { text: 'Actions', value: 'actions', sortable: false, filterable: false }
      ],
      deleteDialog: false,
      deleteTargets: [],
      showDeleteReview: false,
      deleteConfirmation: '',
      deletePassword: '',
      deleteLoading: false,
      deleteError: '',
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
      connectionId: '',
      connectedCharacters: [],
      receivedMessages: [],
      messageToSend: '',
      selectedCharacter: null,
      privateConnector: null,
      pendingConnections: [],
      heartbeatInterval: null,
      pendingCheckInterval: null,
      connectionReadyTimeout: null,
      registerRetryTimeout: null,
      pendingResponseTimeouts: new Set(),
      connectionGeneration: 0,
      adminSessionStarted: false,
      isDestroyed: false
    }
  },
  computed: {
    expectedDeleteConfirmation() {
      if (!this.deleteTargets.length) return ''
      if (this.showDeleteReview) return `DELETE ${this.deleteTargets.length}`
      return this.deleteTargets[0].character_name
    },
    canConfirmDelete() {
      return Boolean(
        this.deleteTargets.length
        && !this.deleteLoading
        && this.deleteConfirmation === this.expectedDeleteConfirmation
        && Boolean(this.deletePassword)
      )
    }
  },
  beforeDestroy() {
    this.isDestroyed = true
    this.cleanupAdminSession()
  },
  methods: {
    async login() {
      if (this.loginLoading || !this.adminUsername || !this.adminPassword) return

      this.loginLoading = true
      this.loginError = ''
      try {
        const response = await this.callHttpTrigger({
          command: 'checkPassword',
          username: this.adminUsername,
          password: this.adminPassword
        })
        const responseText = (await response.text()).trim()

        if (!response.ok || responseText !== 'superAdmin') {
          throw new Error('No admin access')
        }

        this.admin = true
        this.startAdminSession()
      } catch (error) {
        console.error('Admin login failed:', error)
        this.admin = false
        this.loginError = 'Login failed. Check your admin credentials.'
      } finally {
        this.loginLoading = false
      }
    },

    logout() {
      this.cleanupAdminSession()
      this.admin = false
      this.adminPassword = ''
      this.loginError = ''
      this.activeTab = 0
    },

    startAdminSession() {
      if (this.adminSessionStarted || !this.admin || this.isDestroyed) return

      this.adminSessionStarted = true
      this.supabase = createClient(this.supabaseUrl, this.supabaseKey)
      this.initializeConnection()
      this.startHeartbeat()
      this.checkPendingConnections()
      this.loadCharacterSheets()
    },

    cleanupAdminSession() {
      this.adminSessionStarted = false
      this.connectionGeneration += 1

      if (this.heartbeatInterval) clearInterval(this.heartbeatInterval)
      if (this.pendingCheckInterval) clearInterval(this.pendingCheckInterval)
      if (this.connectionReadyTimeout) clearTimeout(this.connectionReadyTimeout)
      if (this.registerRetryTimeout) clearTimeout(this.registerRetryTimeout)
      this.pendingResponseTimeouts.forEach(timeout => clearTimeout(timeout))

      this.heartbeatInterval = null
      this.pendingCheckInterval = null
      this.connectionReadyTimeout = null
      this.registerRetryTimeout = null
      this.pendingResponseTimeouts.clear()
      this.destroyPrivateConnector()

      this.connectedCharacters = []
      this.pendingConnections = []
      this.receivedMessages = []
      this.selectedCharacter = null
      this.messageToSend = ''
      this.characterSheets = []
      this.characterSheetsError = ''
      this.selectedSheets = []
      this.supabase = null
      this.deleteDialog = false
      this.deleteTargets = []
      this.showDeleteReview = false
      this.deleteConfirmation = ''
      this.deletePassword = ''
      this.deleteLoading = false
      this.deleteError = ''
    },

    callHttpTrigger(payload) {
      return fetch(this.url, {
        method: 'POST',
        body: JSON.stringify(payload)
      })
    },

    initializeConnection() {
      if (!this.isAdminActive()) return

      const generation = ++this.connectionGeneration
      this.privateConnector = new PrivateConnector()
      this.privateConnector.createNewReceiverConnection(
        this.onPlayerOpen,
        this.onPlayerMessage,
        this.onPlayerClose
      )

      this.connectionReadyTimeout = setTimeout(() => {
        this.connectionReadyTimeout = null
        if (!this.isCurrentConnection(generation)) return

        const peerId = this.privateConnector.getPeerConnectionId()
        this.connectionId = peerId ? String(peerId) : ''
        console.log('Admin connection ID set to:', this.connectionId)
        this.registerAdminConnection(generation)
      }, 2000)
    },

    async registerAdminConnection(generation = this.connectionGeneration) {
      if (!this.isCurrentConnection(generation)) return

      if (!this.connectionId && this.privateConnector) {
        const peerId = this.privateConnector.getPeerConnectionId()
        this.connectionId = peerId ? String(peerId) : ''
      }

      if (!this.connectionId) {
        this.registerRetryTimeout = setTimeout(() => {
          this.registerRetryTimeout = null
          this.registerAdminConnection(generation)
        }, 1000)
        return
      }

      try {
        const response = await this.callHttpTrigger({
          command: 'setAdminConnectionId',
          connectionId: this.connectionId
        })
        if (response.ok) console.log('Admin connection ID registered with server')
      } catch (error) {
        console.error('Error registering admin connection ID:', error)
      }
    },

    onPlayerOpen(peerId) {
      if (!this.isAdminActive()) return
      console.log('Character connected to admin with peer ID:', peerId)
      if (!peerId) {
        console.warn('No peerId provided to onPlayerOpen')
        return
      }

      const pendingIndex = this.pendingConnections.findIndex(
        connection => connection.characterPeerId === peerId
      )
      let characterName = `Character ${peerId.substring(0, 8)}...`
      let characterId = null

      if (pendingIndex !== -1) {
        const pendingConnection = this.pendingConnections[pendingIndex]
        characterName = pendingConnection.characterName
        characterId = pendingConnection.characterId
        this.pendingConnections.splice(pendingIndex, 1)
      }

      const existingIndex = this.connectedCharacters.findIndex(
        character => character.peerId === peerId
      )
      const characterInfo = {
        peerId,
        name: characterName,
        characterId,
        connectedAt: new Date().toISOString()
      }

      if (existingIndex === -1) {
        this.connectedCharacters.push(characterInfo)
      } else {
        this.$set(this.connectedCharacters, existingIndex, characterInfo)
      }

      if (this.connectedCharacters.length === 1) this.selectedCharacter = peerId

      if (this.privateConnector) {
        this.privateConnector.sendToCharacter({
          type: 'admin_welcome',
          message: 'Welcome! You are now connected to the admin panel.',
          timestamp: new Date().toISOString()
        }, peerId)
      }
    },

    onPlayerMessage(data, peerId) {
      if (!this.isAdminActive()) return
      const timestamp = new Date().toLocaleTimeString()
      if (!peerId) {
        console.warn('No peerId provided to onPlayerMessage')
        return
      }

      const characterName = this.getCharacterName(peerId)
      let safeContent = 'Unable to display message content'
      try {
        safeContent = JSON.stringify(data, (key, value) => {
          if (
            typeof value === 'object'
            && value !== null
            && ['conn', 'peer', '_events', 'context'].includes(key)
          ) {
            return '[Connection Object]'
          }
          return value
        }, 2)
      } catch (error) {
        console.warn('Could not stringify message data due to circular references:', error)
        safeContent = typeof data === 'string' ? data : '[Complex Object]'
      }

      this.receivedMessages.push({
        timestamp,
        from: characterName,
        content: safeContent,
        peerId
      })

      if (data && data.type === 'character_data') {
        console.log('Received character data:', data.character)
        this.updateCharacterInfo(peerId, data.character)
      } else if (data && data.type === 'character_message') {
        console.log('Character says:', data.content)
      }
    },

    onPlayerClose(peerId) {
      if (!this.isAdminActive()) return
      console.log('Character disconnected from admin:', peerId)
      if (!peerId) {
        console.warn('No peerId provided to onPlayerClose')
        return
      }

      this.connectedCharacters = this.connectedCharacters.filter(
        character => character.peerId !== peerId
      )
      if (this.selectedCharacter === peerId) {
        this.selectedCharacter = this.connectedCharacters.length
          ? this.connectedCharacters[0].peerId
          : null
      }
    },

    getCharacterName(peerId) {
      const character = this.connectedCharacters.find(item => item.peerId === peerId)
      return character ? character.name : `Unknown (${peerId.substring(0, 8)}...)`
    },

    updateCharacterInfo(peerId, characterData) {
      if (!characterData) return
      const characterIndex = this.connectedCharacters.findIndex(
        character => character.peerId === peerId
      )
      if (characterIndex !== -1) {
        const character = this.connectedCharacters[characterIndex]
        this.$set(
          character,
          'name',
          characterData.characterName
            || characterData.name
            || `Character ${peerId.substring(0, 8)}...`
        )
        this.$set(
          character,
          'characterId',
          characterData.uuid || characterData.characterId
        )
      }
    },

    generateNewId() {
      if (!this.isAdminActive()) return
      this.connectionGeneration += 1
      if (this.connectionReadyTimeout) clearTimeout(this.connectionReadyTimeout)
      if (this.registerRetryTimeout) clearTimeout(this.registerRetryTimeout)
      this.connectionReadyTimeout = null
      this.registerRetryTimeout = null
      this.destroyPrivateConnector()
      this.connectedCharacters = []
      this.selectedCharacter = null
      this.initializeConnection()
    },

    destroyPrivateConnector() {
      const connector = this.privateConnector
      this.privateConnector = null
      this.connectionId = ''
      if (!connector) return

      const receivers = connector.receiverConnections
        ? Array.from(connector.receiverConnections.values())
        : []
      receivers.forEach(receiver => {
        try {
          if (receiver.conn) receiver.conn.close()
          if (receiver.peer && !receiver.peer.destroyed) receiver.peer.destroy()
        } catch (error) {
          console.warn('Error closing PeerJS receiver:', error)
        }
      })

      if (connector.receiverConnections) connector.receiverConnections.clear()
      if (connector.playerPeerIdToReceiver) connector.playerPeerIdToReceiver.clear()
      if (connector.senderConnection) connector.disconnect()
    },

    disconnectCharacter(peerId) {
      if (this.privateConnector) {
        this.privateConnector.closeCharacterConnection(peerId)
      }
    },

    sendMessage() {
      const content = this.messageToSend.trim()
      if (!content || !this.selectedCharacter || !this.privateConnector) return

      this.privateConnector.sendToCharacter({
        type: 'admin_message',
        content,
        timestamp: new Date().toISOString()
      }, this.selectedCharacter)
      this.messageToSend = ''
    },

    sendToAll() {
      const content = this.messageToSend.trim()
      if (!content || !this.privateConnector) return

      this.privateConnector.sendToAllCharacters({
        type: 'admin_message',
        content,
        timestamp: new Date().toISOString()
      })
      this.messageToSend = ''
    },

    sendCommand(command) {
      if (!this.selectedCharacter || !this.privateConnector) return
      this.privateConnector.sendToCharacter({
        type: 'admin_command',
        command,
        timestamp: new Date().toISOString()
      }, this.selectedCharacter)
      console.log(`Sent command '${command}' to ${this.selectedCharacter}`)
    },

    startHeartbeat() {
      if (this.heartbeatInterval || !this.isAdminActive()) return
      this.heartbeatInterval = setInterval(async () => {
        if (!this.isAdminActive() || !this.connectionId) return
        try {
          await this.callHttpTrigger({
            command: 'adminHeartbeat',
            connectionId: this.connectionId
          })
        } catch (error) {
          console.error('Error sending admin heartbeat:', error)
        }
      }, 10000)
    },

    checkPendingConnections() {
      if (this.pendingCheckInterval || !this.isAdminActive()) return
      this.fetchPendingConnections()
      this.pendingCheckInterval = setInterval(this.fetchPendingConnections, 5000)
    },

    async fetchPendingConnections() {
      if (!this.isAdminActive()) return
      try {
        const response = await this.callHttpTrigger({
          command: 'getPendingConnections'
        })
        if (!response.ok || !this.isAdminActive()) return

        const pendingData = await response.text()
        if (!pendingData || pendingData === 'no_pending') {
          this.pendingConnections = []
          return
        }

        const pending = JSON.parse(pendingData)
        this.pendingConnections = Array.isArray(pending) ? pending : []
        if (this.pendingConnections.length > 0 && this.connectionId) {
          for (const connection of this.pendingConnections) {
            await this.respondToConnection(connection)
          }
        }
      } catch (error) {
        console.error('Error checking pending connections:', error)
      }
    },

    async respondToConnection(connection) {
      if (!this.privateConnector || !this.isAdminActive()) return
      try {
        console.log(
          `Responding to connection request from character ${connection.characterName} with peer ID ${connection.characterPeerId}`
        )
        const generation = this.connectionGeneration
        const newReceiver = this.privateConnector.createNewReceiverConnection(
          this.onPlayerOpen,
          this.onPlayerMessage,
          this.onPlayerClose
        )

        const timeout = setTimeout(async () => {
          this.pendingResponseTimeouts.delete(timeout)
          if (!this.isCurrentConnection(generation)) return

          try {
            const newConnectionId = newReceiver.getId()
            if (!newConnectionId) return

            await this.callHttpTrigger({
              command: 'setPlayerConnectionId',
              characterId: connection.characterId,
              connectionId: newConnectionId
            })
            await this.callHttpTrigger({
              command: 'clearPlayerRequest',
              characterId: connection.characterId
            })
            console.log(
              `Created new receiver connection ${newConnectionId} for character ${connection.characterName}`
            )
          } catch (error) {
            console.error('Error completing pending connection response:', error)
          }
        }, 1000)
        this.pendingResponseTimeouts.add(timeout)
      } catch (error) {
        console.error('Error responding to connection:', error)
      }
    },

    isAdminActive() {
      return this.admin && this.adminSessionStarted && !this.isDestroyed
    },

    isCurrentConnection(generation) {
      return this.isAdminActive()
        && generation === this.connectionGeneration
        && Boolean(this.privateConnector)
    },

    clearMessages() {
      this.receivedMessages = []
    },

    async loadCharacterSheets() {
      if (!this.supabase || !this.admin) return
      this.characterSheetsLoading = true
      this.characterSheetsError = ''
      try {
        const { data, error } = await this.supabase
          .from('character_sheet')
          .select(
            'id, character_name, owner_name, locked, sync_revision, character_data_last_change, instance_of'
          )
          .order('character_data_last_change', { ascending: false })

        if (error) throw error
        this.characterSheets = data || []
        const sheetIds = new Set(this.characterSheets.map(sheet => sheet.id))
        this.selectedSheets = this.selectedSheets.filter(sheet => sheetIds.has(sheet.id))
      } catch (error) {
        console.error('Error loading character sheets:', error)
        this.characterSheetsError = error.message || 'Unable to load character sheets.'
      } finally {
        this.characterSheetsLoading = false
      }
    },

    async copyUuid(uuid) {
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(uuid)
        } else {
          const textArea = document.createElement('textarea')
          textArea.value = uuid
          textArea.setAttribute('readonly', '')
          textArea.style.position = 'fixed'
          textArea.style.opacity = '0'
          document.body.appendChild(textArea)
          textArea.select()
          document.execCommand('copy')
          document.body.removeChild(textArea)
        }
        this.showSnackbar('UUID copied to clipboard.', 'success')
      } catch (error) {
        console.error('Unable to copy UUID:', error)
        this.showSnackbar('Unable to copy UUID.', 'error')
      }
    },

    openCharacterSheet(uuid) {
      const route = this.$router.resolve({
        path: '/site/characterSheet',
        query: { uuid }
      })
      const openedWindow = window.open(route.href, '_blank')
      if (openedWindow) openedWindow.opener = null
    },

    openDeleteDialog(character) {
      this.deleteTargets = [character]
      this.showDeleteReview = false
      this.deleteConfirmation = ''
      this.deletePassword = ''
      this.deleteError = ''
      this.deleteDialog = true
    },

    openBulkDeleteDialog() {
      if (!this.selectedSheets.length) return
      this.deleteTargets = [...this.selectedSheets]
      this.showDeleteReview = true
      this.deleteConfirmation = ''
      this.deletePassword = ''
      this.deleteError = ''
      this.deleteDialog = true
    },

    closeDeleteDialog() {
      if (this.deleteLoading) return
      this.deleteDialog = false
      this.deleteTargets = []
      this.showDeleteReview = false
      this.deleteConfirmation = ''
      this.deletePassword = ''
      this.deleteError = ''
    },

    async deleteCharacterSheets() {
      if (!this.canConfirmDelete || !this.supabase) return

      const targets = [...this.deleteTargets]
      const password = this.deletePassword
      const client = this.supabase
      this.deleteLoading = true
      this.deleteError = ''

      const deletedIds = []
      const deletedNames = []
      const failures = []

      try {
        for (const target of targets) {
          const { error } = await client.rpc('admin_delete_character_sheet', {
            p_id: target.id,
            p_expected_name: target.character_name,
            p_admin_password: password
          })
          if (error) {
            failures.push({
              name: target.character_name,
              message: error.message || 'Unable to delete this character sheet.'
            })
            continue
          }
          deletedIds.push(target.id)
          deletedNames.push(target.character_name)
        }

        if (this.supabase !== client || !this.isAdminActive()) return

        if (deletedIds.length) {
          const deletedIdSet = new Set(deletedIds)
          this.selectedSheets = this.selectedSheets.filter(sheet => !deletedIdSet.has(sheet.id))
          await this.loadCharacterSheets()
        }

        if (failures.length && !deletedNames.length) {
          this.deleteError = failures
            .map(failure => `${failure.name}: ${failure.message}`)
            .join(' ')
          this.deleteLoading = false
          return
        }

        this.deleteLoading = false
        this.closeDeleteDialog()

        if (failures.length) {
          this.showSnackbar(
            `Deleted ${deletedNames.length} sheet(s). Failed: ${failures.map(f => f.name).join(', ')}.`,
            'warning'
          )
        } else if (deletedNames.length === 1) {
          this.showSnackbar(`${deletedNames[0]} was deleted.`, 'success')
        } else {
          this.showSnackbar(`${deletedNames.length} character sheets were deleted.`, 'success')
        }
      } catch (error) {
        if (this.supabase !== client || !this.isAdminActive()) return
        console.error('Error deleting character sheets:', error)
        this.deleteError = error.message || 'Unable to delete character sheets.'
        this.deleteLoading = false
      }
    },

    formatDate(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      return date.toLocaleString()
    },

    showSnackbar(message, color) {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.snackbar = true
    }
  }
}
</script>

<style scoped>
.admin-page {
  min-height: calc(100vh - 64px);
}

.admin-shell {
  max-width: 1500px;
}

.login-card {
  max-width: 480px;
  margin-top: clamp(24px, 10vh, 96px);
}

.connection-id,
.uuid-cell {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.uuid-cell {
  font-family: monospace;
}

.status-card {
  display: flex;
  align-items: center;
  justify-content: center;
}

.connection-actions {
  align-items: flex-end;
}

.admin-actions,
.row-actions,
.table-controls,
.table-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
}

.admin-actions,
.table-toolbar {
  flex-wrap: wrap;
}

.table-toolbar {
  justify-content: space-between;
}

.table-controls {
  width: min(100%, 640px);
}

.table-controls .v-text-field {
  flex: 1 1 260px;
}

.message-content {
  white-space: pre-wrap;
  word-break: break-word;
}

.character-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.delete-review-list {
  max-height: 240px;
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

@media (max-width: 600px) {
  .table-controls {
    align-items: stretch;
    flex-direction: column;
    width: 100%;
  }

  .table-controls .v-text-field {
    flex-basis: auto;
  }

  .admin-actions > .v-btn {
    flex: 1 1 auto;
    margin: 0;
  }

  .connection-actions {
    margin-left: 8px;
  }
}
</style>
